import {
  clearOnboardingSessionHandled,
  getIsOnboardingHandledInSession,
  markOnboardingHandledInSession
} from './onboardingSessionStorage';
import { OnboardingSessionContext } from './context';
import { OnboardingSessionContextValue, OnboardingSessionProviderProps } from './types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const OnboardingSessionProvider = (props: OnboardingSessionProviderProps) => {
  const {
    children,
    isSessionResolved,
    sessionKey
  } = props;
  const [isHandledInSession, setIsHandledInSession] = useState(() => {
    return isSessionResolved && getIsOnboardingHandledInSession(sessionKey);
  });
  const hasResolvedSessionRef = useRef(isSessionResolved);
  const previousSessionKeyRef = useRef(isSessionResolved ? sessionKey : null);

  useEffect(() => {
    if (!isSessionResolved) return;

    if (!hasResolvedSessionRef.current) {
      hasResolvedSessionRef.current = true;
      previousSessionKeyRef.current = sessionKey;
      setIsHandledInSession(getIsOnboardingHandledInSession(sessionKey));
      return;
    }

    const previousSessionKey = previousSessionKeyRef.current;

    if (previousSessionKey === sessionKey) return;

    clearOnboardingSessionHandled(previousSessionKey);
    clearOnboardingSessionHandled(sessionKey);
    previousSessionKeyRef.current = sessionKey;
    setIsHandledInSession(false);
  }, [isSessionResolved, sessionKey]);

  const markHandledInSession = useCallback((): void => {
    if (!isSessionResolved) return;

    markOnboardingHandledInSession(sessionKey);
    setIsHandledInSession(true);
  }, [isSessionResolved, sessionKey]);
  const value = useMemo<OnboardingSessionContextValue>(() => ({
    isHandledInSession,
    markHandledInSession
  }), [isHandledInSession, markHandledInSession]);

  return (
    <OnboardingSessionContext.Provider value={value}>
      {children}
    </OnboardingSessionContext.Provider>
  );
};

export default OnboardingSessionProvider;